<template>
  <v-container class="pa-0">
    <div>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-badge
            :content="contentBadge"
            :value="showBadge"
            color="error"
            overlap
            left
            offset-x="11"
            offset-y="6"
          >
            <v-btn
              v-bind="attrs"
              icon
              small
              v-on="on"
              @click="openDialogButton"
              style="width: auto; height: auto"
            >
              <my-icon
                :name="'alert-icon'"
                :fill="$vuetify.theme.isDark ? '#fff' : '#030303'"
                :width="18"
                :height="18"
                :cursor-pointer="true"
              />
            </v-btn>
          </v-badge>
        </template>
        <span>{{ $t("history") }}</span>
      </v-tooltip>
    </div>

    <v-dialog
      v-model="contentDialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'"
      width="780"
      content-class="nc-dialog"
    >
      <div class="nc" :class="{ 'nc--mobile': isMobile }" :style="themeVars">
        <!-- header -->
        <header class="nc__head">
          <button v-if="isMobile" type="button" class="nc__icon-btn" @click="contentDialog = false">
            <v-icon>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
          </button>
          <span v-else class="nc__head-icon"><v-icon>mdi-bell-outline</v-icon></span>
          <div class="nc__head-text">
            <div class="nc__title">{{ $t('notifCenterTitle') }}</div>
            <div class="nc__subtitle">
              <template v-if="contentBadge > 0">{{ $t('notifCenterUnreadCount', { n: contentBadge }) }}</template>
              <template v-else>{{ $t('notifCenterAllRead') }}</template>
            </div>
          </div>
          <v-spacer />
          <button
            v-if="unreadOnPage.length"
            type="button"
            class="nc__text-btn"
            :title="$t('notifCenterMarkPage')"
            @click="markPageRead"
          >
            <v-icon small>mdi-check-all</v-icon>
            <span class="nc__hide-xs">{{ $t('notifCenterMarkPage') }}</span>
          </button>
          <button
            v-shortkey="['f5']"
            type="button"
            class="nc__icon-btn"
            :title="$t('refresh') + ' (F5)'"
            @shortkey="getItems"
            @click="getItems"
          >
            <v-icon :class="{ 'nc__spin': loading }">mdi-refresh</v-icon>
          </button>
          <button v-if="!isMobile" type="button" class="nc__icon-btn" :title="$t('close')" @click="contentDialog = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </header>

        <!-- filters -->
        <div class="nc__filters">
          <div class="nc__tabs" role="tablist">
            <button
              v-for="t in modeTabs"
              :key="t.value"
              type="button"
              role="tab"
              class="nc__tab"
              :class="{ on: selectMode === t.value }"
              :aria-selected="selectMode === t.value"
              @click="selectMode = t.value"
            >
              {{ $t(t.label) }}
              <span v-if="t.value === 'unread' && contentBadge > 0" class="nc__tab-count">{{ contentBadge }}</span>
            </button>
          </div>
          <div class="nc__search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" :placeholder="$t('search')">
            <button v-if="search" type="button" @click="search = null"><v-icon small>mdi-close</v-icon></button>
          </div>
        </div>

        <!-- list -->
        <div ref="list" class="nc__body">
          <template v-if="loading && !items.length">
            <div v-for="n in 5" :key="'s' + n" class="nc__skel">
              <v-skeleton-loader type="list-item-avatar-two-line" />
            </div>
          </template>

          <div v-else-if="!items.length" class="nc__empty">
            <span class="nc__empty-icon"><v-icon large>mdi-bell-check-outline</v-icon></span>
            <div class="nc__empty-title">{{ $t('notifCenterEmpty') }}</div>
            <div class="nc__empty-sub">{{ search ? $t('notifCenterEmptySearch') : $t('notifCenterEmptySub') }}</div>
          </div>

          <template v-else>
            <section v-for="g in groups" :key="g.key" class="nc__group">
              <div class="nc__day">{{ g.label }}</div>
              <article
                v-for="item in g.items"
                :key="item.id"
                class="nc__item"
                :class="{ 'nc__item--unread': item.seen !== 'active', 'nc__item--link': !!item.reference_id }"
                @click="item.reference_id && link(item)"
              >
                <span class="nc__item-icon" :class="'nc__item-icon--' + kindOf(item).tone">
                  <v-icon small>{{ kindOf(item).icon }}</v-icon>
                </span>
                <div class="nc__item-main">
                  <div class="nc__item-title">{{ item.title }}</div>
                  <div v-if="item.content" class="nc__item-content">{{ item.content }}</div>
                  <div class="nc__item-meta">
                    <span><v-icon x-small>mdi-clock-outline</v-icon>{{ timeOf(item) }}</span>
                    <span v-if="item.user"><v-icon x-small>mdi-account-outline</v-icon>{{ item.user.firstname }} {{ item.user.lastname }}</span>
                    <span v-if="item.reference_id" class="nc__item-open">
                      <v-icon x-small>mdi-open-in-new</v-icon>{{ $t('notifCenterOpen') }}
                    </span>
                  </div>
                </div>
                <div class="nc__item-side" @click.stop>
                  <button
                    v-if="item.seen !== 'active'"
                    type="button"
                    class="nc__seen-btn"
                    :title="$t('iRealized')"
                    @click="setSeen(item)"
                  >
                    <v-icon small>mdi-check</v-icon>
                    <span class="nc__hide-xs">{{ $t('iRealized') }}</span>
                  </button>
                  <span v-else class="nc__seen" :title="$t('seen')"><v-icon small>mdi-check-all</v-icon></span>
                </div>
              </article>
            </section>
          </template>
        </div>

        <!-- pagination -->
        <footer v-if="totalItems" class="nc__foot">
          <span class="nc__range">{{ $t('notifCenterRange', { from: rangeFrom, to: rangeTo, total: totalItems }) }}</span>
          <v-spacer />
          <select v-if="!isMobile" v-model.number="perPage" class="nc__per">
            <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">{{ n }} / {{ $t('notifCenterPage') }}</option>
          </select>
          <button type="button" class="nc__icon-btn" :disabled="page <= 1" @click="goPage(page - 1)">
            <v-icon>{{ $vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
          </button>
          <span class="nc__page">{{ page }} / {{ pageCount || 1 }}</span>
          <button type="button" class="nc__icon-btn" :disabled="page >= pageCount" @click="goPage(page + 1)">
            <v-icon>{{ $vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
          </button>
        </footer>
      </div>
    </v-dialog>

    <v-dialog
      v-model="notificationDialog"
      :fullscreen="true"
      :hide-overlay="true"
      width="400"
    >
      <div v-if="notificationItem" class="nc-single">
        <button type="button" class="nc__icon-btn" @click="closeWindow"><v-icon>mdi-close</v-icon></button>
        <span class="nc-single__icon"><v-icon large>mdi-bell-ring-outline</v-icon></span>
        <h3>{{ notificationItem.title }}</h3>
        <p>{{ notificationItem.body }}</p>
      </div>
    </v-dialog>

    <v-snackbar v-model="alertDialog" :timeout="3000" top right color="primary">
      {{ $t("you") }} {{ unnotifiedCount }} {{ $t("haveNotifications") }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showContentDialog">{{ $t("showNotifications") }}</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
// Redesigned copy of UserHistory.vue (original untouched): same API calls,
// badge logic and public methods (openDialogButton / openDialog /
// showContentDialog). The data table is replaced by a grouped list with its
// own pagination driving the same `options` / `page` state.
import MyIcon from "@/views/dashboard/components/general/icon/MyIcon.vue";

export default {
  name: "IndexNotification",
  components: {
    MyIcon,
  },
  props: ["userProp", "typeProp", "refIdProp", "modeProp"],
  data() {
    return {
      contentBadge: 0,
      alertDialog: false,
      notificationDialog: false,
      showBadge: false,
      contentDialog: false,
      search: null,
      content: null,
      unnotifiedCount: 0,
      pageCount: 1,
      page: 1,
      items: [],
      selectMode: "unread",
      refId: null,
      totalItems: null,
      type: null,
      notificationBrowser: null,
      selectedHeaders: null,
      selected: [],
      headers: null,
      timer: 0,
      loading: false,
      showSelect: false,
      notificationItem: null,
      user: null,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortBy: [],
        sortDesc: [],
      },
      product: null,
    };
  },
  computed: {
    themeVars() {
      var t = this.$vuetify.theme.currentTheme || {};
      var p = typeof t.primary === "string" ? t.primary : "#0066ff";
      return { "--nc-primary": p };
    },
    modeTabs() {
      return [
        { value: "unread", label: "notifCenterUnread" },
        { value: "read", label: "notifCenterRead" },
        { value: "all", label: "notifCenterAll" },
      ];
    },
    perPage: {
      get() {
        return this.options.itemsPerPage;
      },
      set(n) {
        this.page = 1;
        this.options = { ...this.options, itemsPerPage: n, page: 1 };
      },
    },
    rangeFrom() {
      return this.from || (this.page - 1) * this.options.itemsPerPage + 1;
    },
    rangeTo() {
      return Math.min(this.rangeFrom + this.items.length - 1, this.totalItems || 0);
    },
    unreadOnPage() {
      return this.items.filter((i) => i.seen !== "active");
    },
    // Group by the date part of `time` ("09 Aban 1405 - 22:19").
    groups() {
      var out = [];
      var cur = null;
      this.items.forEach((item) => {
        var key = this.dayOf(item);
        if (!cur || cur.key !== key) {
          cur = { key: key, label: key, items: [] };
          out.push(cur);
        }
        cur.items.push(item);
      });
      return out;
    },
    showHeaders() {
      var app = this;
      return this.headers.filter(function (s) {
        if (app.typeForm === "service") {
          if (s.value === "stock" || s.value === "order_point") {
          } else {
            return app.selectedHeaders.includes(s);
          }
        } else {
          return app.selectedHeaders.includes(s);
        }
      });
    },
    excelItems: function () {
      var app = this;
      var list = [];
      this.items.map((d, index) => {
        list.push({
          title: app.$t(d.title),
          content: app.$t(d.content),
        });
      });
      return list;
    },
  },
  watch: {
    selectMode(val) {
      this.page = 1;
      this.options = { ...this.options, page: 1 };
      this.getUnreadNotifications();
      this.getItems();
    },
    contentDialog(open) {
      if (open && this.isMobile) {
        var app = this;
        window.history.pushState({ ...(window.history.state || {}), ncDialog: true }, "");
        this.onPop = function () {
          window.removeEventListener("popstate", app.onPop);
          app.onPop = null;
          app.contentDialog = false;
        };
        window.addEventListener("popstate", this.onPop);
      } else if (!open && this.onPop) {
        window.removeEventListener("popstate", this.onPop);
        this.onPop = null;
        if (window.history.state && window.history.state.ncDialog) window.history.back();
      }
    },
    contentBadge(val) {
      if (val && val > 0) {
        this.showBadge = true;
      } else {
        this.showBadge = false;
      }
    },
    userProp(val) {
      this.user = val;
    },
    refIdProp(val) {
      this.refId = val;
    },
    typeProp(val) {
      this.type = val;
    },
    invoiceProp(val) {
      if (this.invoiceProp && val.tax_payer) {
        this.content = JSON.parse(val.tax_payer.reponse);
      } else {
        this.content = null;
      }
    },
    search() {
      var app = this;
      app.loading = true;
      app.page = 1;
      app.options.page = 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        app.getItems();
      }, 600);
    },
    options: {
      handler() {
        var app = this;
        app.sortBy = [];
        app.options.sortBy.map(function (value, key) {
          const index = app.headers.findIndex((h) => h.value === value);
          app.sortBy.push(app.headers[index].table);
        });
        this.getItems();
      },
      deep: true,
    },
    sortBy() {
      const index = this.headers.findIndex((h) => h.value === this.sortBy);
      if (index > -1) {
        this.sortBy = this.headers[index].table;
      }
    },
  },
  beforeMount() {
    this.headers = [
      {
        text: this.$t("row"),
        align: "start",
        sortable: false,
        value: "row",
        width: 50,
      },
      {
        text: "#",
        sortable: false,
        value: "ref_action",
        width: 50,
      },
      {
        text: this.$t("title"),
        sortable: false,
        value: "title",
        width: 400,
      },
      {
        text: this.$t("content"),
        sortable: false,
        value: "content",
        width: 300,
      },
      {
        text: this.$t("seen"),
        sortable: false,
        value: "seen",
        width: 100,
      },
      {
        text: this.$t("time"),
        sortable: false,
        value: "time",
        width: 100,
      },
    ];
    this.selectedHeaders = this.headers;
    if (this.$route.query.item) {
      this.notificationItem = JSON.parse(this.$route.query.item);
      this.notificationDialog = true;
      this.setSeen(this.notificationItem);
    }
  },
  mounted() {
    this.type = this.typeProp;
    this.refId = this.refIdProp;
    this.user = this.userProp;
    if (!this.$route.query.item) {
      this.getUnnotifiedCount();
      this.getUnreadNotifications();
      this.getItems();
      this.getUnnotified();
      // var app = this
      // this.accurateTimer(function () {
      //   app.getUnnotified()
      // })
    }
  },
  methods: {
    dayOf(item) {
      var t = String(item.time || "");
      var i = t.lastIndexOf(" - ");
      return i > -1 ? t.slice(0, i) : t;
    },
    timeOf(item) {
      var t = String(item.time || "");
      var i = t.lastIndexOf(" - ");
      return i > -1 ? t.slice(i + 3) : t;
    },
    kindOf(item) {
      var type = item.reference_type;
      if (type === "task") return { icon: "mdi-clipboard-check-outline", tone: "task" };
      if (["sell", "presale", "returnSell", "purchase", "returnPurchase"].includes(type)) {
        return { icon: "mdi-receipt-text-outline", tone: "invoice" };
      }
      if (["recieveCheck", "payCheck"].includes(type)) return { icon: "mdi-checkbook", tone: "check" };
      return { icon: "mdi-bell-outline", tone: "default" };
    },
    goPage(n) {
      if (n < 1 || (this.pageCount && n > this.pageCount)) return;
      this.page = n;
      this.options = { ...this.options, page: n };
      if (this.$refs.list) this.$refs.list.scrollTop = 0;
    },
    markPageRead() {
      this.unreadOnPage.slice().forEach((item) => this.setSeen(item));
    },
    closeWindow() {
      window.open("", "_self", "");
      window.close();
    },
    showContentDialog() {
      this.alertDialog = false;
      this.contentDialog = true;
      // this.notifyMe()
    },
    openDialogButton() {
      this.type = this.typeProp;
      this.refId = null;
      this.user = null;
      this.contentDialog = true;
    },
    openDialog(id, type, user) {
      this.type = type;
      this.refId = id;
      this.user = user;
      this.contentDialog = true;
    },
    getUnreadNotifications() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "notifications", {
          params: {
            getUnreadNotifications: "active",
          },
        })
        .then((res) => {
          res = res.data;
          app.contentBadge = res;
          if (app.contentBadge > 0 && !app.contentDialog) {
            // app.alertDialog = true
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    getUnnotifiedCount() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "notifications", {
          params: {
            getUnnotifiedCount: "active",
          },
        })
        .then((res) => {
          res = res.data;
          app.unnotifiedCount = res;
          if (app.unnotifiedCount > 0 && !app.contentDialog) {
            app.alertDialog = true;
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    getUnnotified() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "notifications", {
          params: {
            getUnnotified: "active",
          },
        })
        .then((res) => {
          res = res.data;
          var data = null;
          app.unnotifiedCount = res.unnotifiedCount;
          app.contentBadge = res.unseenCount;
          if (app.unnotifiedCount > 0) {
            app.alertDialog = true;
          }
          if (res.data && res.data.length > 0) {
            app.getUnreadNotifications();
            res.data.map(function (value, index) {
              data = {
                title: value.title,
                body: value.content,
                notifications_users_id: value.notifications_users_id,
              };
              app.notifyMe(data);
            });
          }
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    link(item) {
      if (item.reference_type === "task") {
        this.$router.push({ name: "taskEdit", params: { id: item.reference_id } });
      }
      var invoiceType = ["sell", "presale", "returnSell", "purchase", "returnPurchase"];
      if (invoiceType.includes(item.reference_type)) {
        this.$router.push({
          name: "invoicePrint",
          params: { id: item.uid, tabName: item.reference_id },
        });
      }

      var checksType = ["recieveCheck", "payCheck"];
      if (checksType.includes(item.reference_type)) {
        this.$router.push({
          name: this.type + "Edit",
          params: { id: item.reference_id },
        });
      }

      this.contentDialog = false;
    },
    getItems() {
      var app = this;
      app.loading = true;
      app.$axios
        .get(this.$endpoint + "notifications", {
          params: {
            search: app.search,
            page: app.options.page,
            options: app.options,
            selectMode: app.selectMode,
          },
        })
        .then((res) => {
          res = res.data;
          app.items = res.data || [];
          app.totalItems = res.total;
          app.pageCount = res.last_page;
          app.from = res.from;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    setSeen(item) {
      var app = this;
      app.loading = true;
      item.seen = "active";
      if (app.contentBadge > 0) {
        app.contentBadge--;
      }
      app.$axios
        .get(this.$endpoint + "notifications", {
          params: {
            setSeen: "active",
            id: item.notifications_users_id,
          },
        })
        .then((res) => {
          res = res.data;
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    notifyMe(data) {
      var app = this;
      var icon = "https://abr.abriron.com/favicon.png";
      var body = data.body ? data.body : " ";
      var title = data.title;
      if (!("Notification" in window)) {
        alert("This browser does not support desktop notification");
      } else if (Notification.permission === "granted") {
        this.notificationBrowser = new Notification(title, { body, icon });
        this.notificationBrowser.addEventListener("click", function () {
          window.open(
            window.location.href + "?item=" + JSON.stringify(data),
            "_blank",
            "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400"
          );
          app.showContentDialog();
        });
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            title = this.$t("notificationEnable");
            this.notificationBrowser = new Notification(title, { body, icon });
          }
        });
      }
    },
    accurateTimer(fn, time = 60000) {
      let nextAt, timeout;
      nextAt = new Date().getTime() + time;
      const wrapper = () => {
        nextAt += time;
        timeout = setTimeout(wrapper, nextAt - new Date().getTime());
        fn();
      };
      const cancel = () => clearTimeout(timeout);
      timeout = setTimeout(wrapper, nextAt - new Date().getTime());

      return { cancel };
    },
  },
};
</script>

<style scoped>
::v-deep .v-badge__badge {
  right: 6px !important;
  top: -8px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 9px !important;
  height: auto !important;
  min-height: 14px !important;
  min-width: unset !important;
  width: max-content !important;
  max-width: none !important;
  padding: 1px 6px !important;
  line-height: 1.1 !important;
  white-space: nowrap !important;
  border-radius: 999px !important;
  box-sizing: content-box !important;
}

.nc {
  --nc-primary: #0066ff;
  display: flex;
  flex-direction: column;
  height: min(82vh, 760px);
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}
.theme--dark .nc {
  background: #1e1e1e;
}
.nc--mobile {
  height: 100%;
  border-radius: 0;
}

/* header */
.nc__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #eef0f4;
}
.theme--dark .nc__head {
  border-color: #333;
}
.nc__head-icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 102, 255, 0.1);
}
.nc__head-icon .v-icon {
  color: var(--nc-primary) !important;
}
.nc__title {
  font-weight: 700;
  font-size: 1rem;
}
.nc__subtitle {
  font-size: 0.75rem;
  color: #8a94a6;
}
.nc__icon-btn {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.nc__icon-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}
.nc__icon-btn:disabled {
  opacity: 0.35;
}
.nc__icon-btn .v-icon {
  color: #4b5563 !important;
}
.theme--dark .nc__icon-btn .v-icon {
  color: #ddd !important;
}
.nc__text-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: var(--nc-primary);
  transition: background 0.15s;
}
.nc__text-btn .v-icon {
  color: inherit !important;
}
.nc__text-btn:hover {
  background: rgba(0, 102, 255, 0.08);
}
.nc__spin {
  animation: nc-spin 0.8s linear infinite;
}
@keyframes nc-spin {
  to {
    transform: rotate(360deg);
  }
}

/* filters */
.nc__filters {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #eef0f4;
}
.theme--dark .nc__filters {
  border-color: #333;
}
.nc__tabs {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: #eef1f6;
  border-radius: 6px;
}
.theme--dark .nc__tabs {
  background: #2a2a2a;
}
.nc__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #5b6475;
  white-space: nowrap;
}
.nc__tab.on {
  background: #fff;
  color: var(--nc-primary);
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.theme--dark .nc__tab.on {
  background: #1e1e1e;
}
.nc__tab-count {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #e53935;
  color: #fff;
  font-size: 0.66rem;
  line-height: 18px;
  text-align: center;
}
.nc__search {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 10px;
  border: 1px solid #dfe3ea;
  border-radius: 6px;
  background: #f8f9fb;
  transition: border-color 0.15s, background 0.15s;
}
.nc__search:focus-within {
  border-color: var(--nc-primary);
  background: #fff;
}
.theme--dark .nc__search {
  background: #121212;
  border-color: #3a3a3a;
}
.nc__search input {
  flex: 1 1 auto;
  min-width: 0;
  outline: none;
  font-size: 0.85rem;
  color: inherit;
}
.nc__search .v-icon {
  color: #8a94a6 !important;
}

/* list */
.nc__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0 12px;
  background: #fafbfc;
}
.theme--dark .nc__body {
  background: #191919;
}
.nc__skel {
  padding: 4px 16px;
}
.nc__group + .nc__group {
  margin-top: 4px;
}
.nc__day {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 16px 6px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #8a94a6;
  background: #fafbfc;
}
.theme--dark .nc__day {
  background: #191919;
}
.nc__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 12px 8px;
  padding: 12px;
  background: #fff;
  border: 1px solid #e8ebf0;
  border-radius: 6px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.theme--dark .nc__item {
  background: #1e1e1e;
  border-color: #333;
}
.nc__item--link {
  cursor: pointer;
}
.nc__item--link:hover {
  border-color: #cfd6e0;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.06);
}
.nc__item--unread {
  border-inline-start: 3px solid var(--nc-primary);
}
.nc__item--unread .nc__item-title {
  font-weight: 700;
}
.nc__item-icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef1f6;
}
.nc__item-icon .v-icon {
  color: #5b6475 !important;
}
.nc__item-icon--task {
  background: #e8f5e9;
}
.nc__item-icon--task .v-icon {
  color: #2e7d32 !important;
}
.nc__item-icon--invoice {
  background: #e8f0ff;
}
.nc__item-icon--invoice .v-icon {
  color: #1e5bd6 !important;
}
.nc__item-icon--check {
  background: #fff4e0;
}
.nc__item-icon--check .v-icon {
  color: #b26a00 !important;
}
.nc__item-main {
  flex: 1 1 auto;
  min-width: 0;
}
.nc__item-title {
  font-size: 0.86rem;
  font-weight: 500;
  line-height: 1.7;
  word-break: break-word;
}
.nc__item-content {
  margin-top: 4px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #f4f6f9;
  font-size: 0.8rem;
  color: #4b5563;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}
.theme--dark .nc__item-content {
  background: #262626;
  color: #ccc;
}
.nc__item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  font-size: 0.72rem;
  color: #8a94a6;
}
.nc__item-meta > span {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.nc__item-meta .v-icon {
  color: inherit !important;
}
.nc__item-open {
  color: var(--nc-primary);
  font-weight: 600;
}
.nc__item-side {
  flex: 0 0 auto;
  align-self: center;
}
.nc__seen-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid var(--nc-primary);
  color: var(--nc-primary);
  font-size: 0.76rem;
  white-space: nowrap;
  transition: background 0.15s;
}
.nc__seen-btn .v-icon {
  color: inherit !important;
}
.nc__seen-btn:hover {
  background: rgba(0, 102, 255, 0.08);
}
.nc__seen {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
}
.nc__seen .v-icon {
  color: #2e7d32 !important;
}
.nc__empty {
  height: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 24px;
}
.nc__empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 102, 255, 0.08);
  margin-bottom: 6px;
}
.nc__empty-icon .v-icon {
  color: var(--nc-primary) !important;
}
.nc__empty-title {
  font-weight: 700;
}
.nc__empty-sub {
  font-size: 0.8rem;
  color: #8a94a6;
}

/* footer */
.nc__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-top: 1px solid #eef0f4;
  background: #fff;
}
.theme--dark .nc__foot {
  background: #1e1e1e;
  border-color: #333;
}
.nc__range,
.nc__page {
  font-size: 0.78rem;
  color: #6b7280;
  white-space: nowrap;
}
.nc__page {
  min-width: 56px;
  text-align: center;
}
.nc__per {
  height: 32px;
  padding: 0 8px;
  border: 1px solid #dfe3ea;
  border-radius: 6px;
  font-size: 0.78rem;
  color: inherit;
  background: transparent;
  margin-inline-end: 6px;
}

/* mobile */
.nc--mobile .nc__head {
  padding: 10px 12px;
}
.nc--mobile .nc__filters {
  flex-direction: column;
  align-items: stretch;
  padding: 10px 12px;
}
.nc--mobile .nc__tabs {
  display: flex;
}
.nc--mobile .nc__tab {
  flex: 1 1 0;
  justify-content: center;
}
.nc--mobile .nc__item {
  margin: 0 10px 8px;
  padding: 10px;
}
.nc--mobile .nc__item-side {
  align-self: flex-start;
}
.nc--mobile .nc__hide-xs {
  display: none;
}
.nc--mobile .nc__seen-btn {
  width: 32px;
  padding: 0;
  justify-content: center;
}
.nc--mobile .nc__foot {
  padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
}

.nc-single {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: #fff;
  text-align: center;
}
.nc-single > .nc__icon-btn {
  align-self: flex-start;
}
.nc-single__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 102, 255, 0.1);
  margin-top: 24px;
}
.nc-single__icon .v-icon {
  color: #0066ff !important;
}
</style>
